// Helper de streaming SSE do chat do Assistente Geral (Frente 3, G08/G10).
//
// Usa `fetch` direto em vez de axios (precisa do `ReadableStream` pra ler
// token-a-token; `EventSource` só faz GET e não manda `Authorization`).
//
// DT-12: o `fetch` NÃO passa pelo interceptor 402 do axios → o 402 é tratado
// aqui manualmente (`handleAssistantQuotaError`), abrindo o `AssistantQuotaModal`.
// Sem isso, estourar cota no chat não abriria o modal.

import config from '@/config';
import {
  AssistantQuotaErrorResponse,
  AssistantStreamDonePayload,
  AssistantStreamErrorPayload,
  AssistantStreamHandlers,
  AssistantStreamQuestionPayload,
  AssistantStreamStatus,
  AssistantStreamToolEvent,
  AssistantStreamFileEvent,
  ElicitationAnswer,
  WebCitation,
} from '@/types/assistant';
import { handleAssistantQuotaError } from '@/utils/assistantQuotaErrorHandler';

const REFRESH_PATH = '/auth/refresh/';

/** Erro de cota (HTTP 402) preservando o payload estruturado do backend. */
export class AssistantQuotaStreamError extends Error {
  data: AssistantQuotaErrorResponse;

  constructor(data: AssistantQuotaErrorResponse) {
    super(data.message || 'Cota excedida.');
    this.name = 'AssistantQuotaStreamError';
    this.data = data;
  }
}

export function isAssistantQuotaStreamError(err: unknown): err is AssistantQuotaStreamError {
  return (
    err instanceof AssistantQuotaStreamError ||
    (err as { name?: string })?.name === 'AssistantQuotaStreamError'
  );
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  try {
    const resp = await fetch(`${config.api.baseURL}${REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { access_token?: string };
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      return data.access_token;
    }
  } catch {
    /* swallow — caller trata como sessão expirada */
  }
  return null;
}

async function postStream(url: string, body: string, signal?: AbortSignal): Promise<Response> {
  const token = localStorage.getItem('access_token');
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  });
}

interface ParsedBlock {
  event: string;
  data: unknown;
}

/** Parseia um bloco SSE bruto (linhas `event:`/`data:`). Retorna null se inválido. */
function parseBlock(raw: string): ParsedBlock | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const sep = line.indexOf(':');
    if (sep < 0) continue;
    const field = line.slice(0, sep);
    const value = line.slice(sep + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}

export interface StreamMessageOptions {
  attachmentIds?: string[];
}

/** Consome o corpo SSE de uma `Response` já validada, despachando os eventos pros handlers. */
async function consumeStream(response: Response, handlers: AssistantStreamHandlers): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });

      let idx = buffer.indexOf('\n\n');
      while (idx >= 0) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseBlock(raw);
        if (parsed) {
          switch (parsed.event) {
            case 'status':
              handlers.onStatus?.((parsed.data as AssistantStreamStatus) ?? {});
              break;
            case 'token':
              handlers.onToken?.((parsed.data as { content: string }).content);
              break;
            case 'tool':
              handlers.onTool?.(parsed.data as AssistantStreamToolEvent);
              break;
            case 'file':
              handlers.onFile?.(parsed.data as AssistantStreamFileEvent);
              break;
            case 'citation':
              handlers.onCitation?.(parsed.data as WebCitation);
              break;
            // E01/E02: o bot pausou e perguntou — encerra o stream (sem `done`).
            case 'question':
              handlers.onQuestion?.(parsed.data as AssistantStreamQuestionPayload);
              break;
            case 'done':
              handlers.onDone?.(parsed.data as AssistantStreamDonePayload);
              break;
            case 'error':
              handlers.onError?.(parsed.data as AssistantStreamErrorPayload);
              break;
            default:
            // ignora eventos desconhecidos (forward-compat)
          }
        }
        idx = buffer.indexOf('\n\n');
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* swallow */
    }
  }
}

/**
 * POST + refresh em 401 + tratamento de 402 (DT-12) + consumo do stream SSE.
 *
 * Compartilhado por `streamAssistantMessage` (enviar mensagem) e `submitElicitation`
 * (responder a pergunta do bot) — ambos retomam o MESMO formato de eventos do backend.
 * Lança `AssistantQuotaStreamError` em 402 (já tendo aberto o modal) e `Error` genérico
 * em outros 4xx/5xx ou stream cortado. `AbortError` (signal) propaga como cancelamento.
 */
async function runStream(
  url: string,
  body: string,
  handlers: AssistantStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  let response = await postStream(url, body, signal);

  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (!newToken) {
      throw new Error('Sessão expirada. Faça login de novo.');
    }
    response = await postStream(url, body, signal);
  }

  if (response.status === 402) {
    const data = (await response.json()) as AssistantQuotaErrorResponse;
    // DT-12: o interceptor do axios não roda no fetch direto — dispara o modal aqui.
    if (data?.error === 'quota_exceeded') {
      handleAssistantQuotaError(data);
    }
    throw new AssistantQuotaStreamError(data);
  }

  if (!response.ok || !response.body) {
    throw new Error(`stream_failed:${response.status}`);
  }

  await consumeStream(response, handlers);
}

/**
 * Envia uma mensagem pro Assistente via SSE.
 *
 * Backend (G08) persiste a `user_message` antes do 1º byte e emite os eventos
 * `status` (thinking) → `token`* / `tool` / `file` / `citation` → `done` (ou
 * `error`, ou `question` se o bot pausar pra perguntar — E01). Esse helper
 * transforma os bytes em chamadas tipadas pros `handlers`.
 */
export async function streamAssistantMessage(
  conversationId: string,
  content: string,
  handlers: AssistantStreamHandlers,
  options: StreamMessageOptions = {},
  signal?: AbortSignal,
): Promise<void> {
  const url = `${config.api.baseURL}/assistant/conversations/${conversationId}/messages/`;
  const body = JSON.stringify({
    content,
    ...(options.attachmentIds?.length ? { attachment_ids: options.attachmentIds } : {}),
  });
  return runStream(url, body, handlers, signal);
}

/**
 * E02: responde a pergunta pendente (`ask_user`) e RETOMA o stream do bot (E01).
 *
 * Mesmo ciclo de eventos do `streamAssistantMessage` — o bot continua de onde parou
 * com a resposta em mãos (pode perguntar de novo → novo `question`). DT-12 tratado.
 */
export async function submitElicitation(
  conversationId: string,
  answers: ElicitationAnswer[],
  handlers: AssistantStreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const url = `${config.api.baseURL}/assistant/conversations/${conversationId}/elicitation/`;
  const body = JSON.stringify({ answers });
  return runStream(url, body, handlers, signal);
}
